import { Injectable } from '@angular/core'
import moment from 'moment'
import numeral from 'numeral'
import { CellValueFn, RowData, RowOptions, TableColumn, TableRow } from 'src/app/shared/atoms/table/table.models'
import { Utils } from '../../../server/src/shared-with-client/utils'
import { Csv } from './model/Csv.model'
import { CsvJson } from './model/CsvJson.model'
import { User } from './parse.service'
import { LegacyCellValueFn, LegacyRowData, LegacyRowOptions, LegacyTableColumn, LegacyTableRow } from './table/table.models'

export interface PseudoFile {
    name: string
    blob: Blob
}

export interface CsvRow {
    id: string
    csvRow: string[]
}

@Injectable()
export class UtilsService {
    getLegacyTableInfoFromCSV(csv: CsvJson, rowOptions: LegacyRowOptions, rowLimit?: number) {
        const columns: LegacyTableColumn<string[]>[] = []
        const rows: LegacyTableRow<string[], undefined>[] = []
        const cellValueFns: LegacyCellValueFn<string[]>[] = csv.headers.map((value, index) => {
            return (row: LegacyRowData<string[]>) => {
                return row[index]
            }
        })

        const limit = rowLimit !== undefined ? (rowLimit < csv.rows.length ? rowLimit : csv.rows.length) : csv.rows.length

        for (let i = 0; i < csv.headers.length; i++) {
            const header = csv.headers[i]
            columns.push(new LegacyTableColumn(header, cellValueFns[i], 'default'))
        }

        for (let i = 0; i < limit; i++) {
            const row = csv.rows[i]
            const id = '' + i

            rows.push(new LegacyTableRow(id, row, columns, rowOptions))
        }

        if (rows.length > 0) {
            const lastRow = rows[rows.length - 1].data
            if (lastRow.length === 1 && lastRow[0] === '') {
                rows.splice(rows.length - 1, 1)
            }
        }

        return {
            columns,
            rows,
        }
    }

    getTableInfoFromCSV(csv: CsvJson, rowOptions: RowOptions, rowLimit?: number) {
        // Todo: Remove Old Version above once everything that uses getTableInfoFromCSV() is updated to use the new Table
        const columns: TableColumn<CsvRow>[] = []

        const rows: TableRow<CsvRow, undefined>[] = []
        const cellValueFns: CellValueFn<CsvRow>[] = csv.headers.map((value, index) => {
            return (row: RowData<CsvRow>) => {
                return row.csvRow[index]
            }
        })

        const limit = rowLimit !== undefined ? (rowLimit < csv.rows.length ? rowLimit : csv.rows.length) : csv.rows.length

        for (let i = 0; i < csv.headers.length; i++) {
            const header = csv.headers[i]
            columns.push(new TableColumn(header, cellValueFns[i], 'default', undefined, true))
        }

        for (let i = 0; i < limit; i++) {
            const row = { id: `${i}`, csvRow: csv.rows[i] }
            const id = '' + i

            rows.push(new TableRow(id, row, columns, rowOptions))
        }

        if (rows.length > 0) {
            const lastRow = rows[rows.length - 1].data
            if (lastRow.csvRow.length === 1 && lastRow.csvRow[0] === '') {
                rows.splice(rows.length - 1, 1)
            }
        }

        return {
            columns,
            rows,
        }
    }

    getCSVRecordCount = (file: PseudoFile): Promise<number> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsText(file.blob)
            reader.onload = () => {
                try {
                    const result: string | ArrayBuffer | null = reader.result

                    if (typeof result !== 'string') {
                        return resolve(0)
                    }

                    const csv = new Csv('', result, '')

                    resolve(csv.rows.length)
                } catch (err) {
                    reject(err)
                }
            }
        })
    }

    isSameUser(a: User | undefined, b: User | undefined): boolean {
        const keyA = a?.id
        const keyB = b?.id
        return keyA === keyB
    }
    isSameUserExpanded(a: User | 'ALL' | 'NONE' | undefined, b: User | 'ALL' | 'NONE' | undefined): boolean {
        const keyA = typeof a === 'string' ? a : a?.id
        const keyB = typeof b === 'string' ? b : b?.id
        return keyA === keyB
    }

    isSameParseObject<T extends Parse.Object>(a: T | undefined, b: T | undefined): boolean {
        if (!a || !b) {
            return a === b
        }

        if (a.className !== b.className) {
            return false
        }

        return a.id === b.id
    }

    formatDate(date: Date, format = 'MM/DD/YYYY h:mm A') {
        if (date) {
            try {
                return moment(date).local().format(format)
            } catch (err) {
                return ''
            }
        }
        return ''
    }

    getDesignEditorUrl(mailingOrderId: string, redirect?: string) {
        const url = `editor/${mailingOrderId}${redirect ? '?redirect=' + redirect : ''}`
        const baseUrl = `editor/${mailingOrderId}`
        const queryParams = {
            redirect,
        }
        return {
            baseUrl,
            url,
            queryParams,
        }
    }

    formatCentsToCurrency(cents: number, format: '$0.00' | '$0,0[.]00' | '$0,0.00') {
        return numeral(cents).divide(100).format(format)
    }

    getRGBfromPaletteValue(dialogValue: string): { r: number; g: number; b: number } | undefined {
        if (!dialogValue) {
            return undefined
        }

        const indexOfOpenParenthesis = dialogValue.indexOf('(')
        const indexOfCloseParenthesis = dialogValue.indexOf(')')

        const rgbSplit = dialogValue.slice(indexOfOpenParenthesis + 1, indexOfCloseParenthesis).split(',')

        if (rgbSplit && rgbSplit.length === 3) {
            return {
                r: +rgbSplit[0],
                g: +rgbSplit[1],
                b: +rgbSplit[2],
            }
        }

        return undefined
    }

    getPaletteValueFromRGB(color: { r: number; g: number; b: number }) {
        if (color && color.r !== undefined && color.g !== undefined && color.b !== undefined) {
            return `rgb(${color.r},${color.g},${color.b})`
        } else {
            return ''
        }
    }

    getMailingOrderBasePath(mailingOrderId: string, withLeadingSlash = false) {
        return Utils.getMailingOrderBasePath(mailingOrderId, { withLeadingSlash })
    }
    /**
     * Check if the window is currently scrolled to the bottom
     * @param padding Additional padding before reaching the end
     */
    isScrolledTotheBottom(padding = 0): boolean {
        const element = document.documentElement

        return element.scrollHeight - Math.abs(element.scrollTop) <= element.clientHeight + padding
    }

    preventCloseEvent($event: Event): void {
        $event.preventDefault()

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $event.returnValue = ''
        return
    }

    getCreditCardImageSource(cardType: string /*Objects.Stripe.PaymentMethodDetails.Card.Brand*/) {
        switch (cardType) {
            case 'amex':
                return '/assets/img/credit-cards/amex.png'
            case 'diners':
                return '/assets/img/credit-cards/diners.png'
            case 'discover':
                return '/assets/img/credit-cards/discover.png'
            case 'jcb':
                return '/assets/img/credit-cards/jcb.png'
            case 'mastercard':
                return '/assets/img/credit-cards/mastercard.png'
            // case 'unionpay': return '/assets/img/credit-cards/unknown.png'
            case 'visa':
                return '/assets/img/credit-cards/visa.png'
            default:
                return './assets/img/credit-cards/unknown.png'
        }
    }

    getCreditCardImageSourceV2(cardType: string /*Objects.Stripe.PaymentMethodDetails.Card.Brand*/) {
        switch (cardType) {
            case 'amex':
                return '/assets/img/new-designs/credit-cards/amex.png'
            case 'diners':
                return '/assets/img/new-designs/credit-cards/diners.png'
            case 'discover':
                return '/assets/img/new-designs/credit-cards/discover.jpg'
            case 'jcb':
                return '/assets/img/new-designs/credit-cards/jcb.png'
            case 'mastercard':
                return '/assets/img/new-designs/credit-cards/mastercard.jpg'
            case 'visa':
                return '/assets/img/new-designs/credit-cards/visa.png'
            default:
                return './assets/img/credit-cards/v2/unknown.png'
        }
    }

    setSessionStorageValue(key: string, value: unknown) {
        sessionStorage.setItem(key, JSON.stringify(value))
    }

    getSessionStorageValue(key: string) {
        const storageItem = sessionStorage.getItem(key)
        if (storageItem) {
            return JSON.parse(storageItem)
        }
    }

    getTruncatedString(name: string, endPosition: number): string {
        return name.length > endPosition ? name.slice(0, endPosition) : name
    }
}
